import { useDisclosure, useToast } from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { hideAppSpinner, showAppSpinner } from "../../../../../../../context/Store/slices/appSpinnerSlice";
import { showErrorToast, showSuccessToast } from "../../../../../../../utils";
import CategoryForm
  from "../../../../../../OrganizationQuestionnaire/components/Categories/components/Category/CategoryForm";
import {
  CategoryGrid
} from "../../../../../../OrganizationQuestionnaire/components/Categories/components/CategoryGrid";
import { DeleteDialog } from "../../../../../../DataEntryPlanning/components/DeleteDialog";
import {
  WasteGeneratedInOperationsModalWrapper
} from "../../../../../../OrganizationQuestionnaire/components/Categories/components/CategoryModalWrappers";
import * as React from "react";
import {
  supplierQuestionnaireWasteGeneratedInOperationsKeys,
  useGetSupplierQuestionnaireWasteGeneratedInOperationsItems,
  useAddSupplierQuestionnaireWasteGeneratedInOperationsItems,
  useDeleteSupplierQuestionnaireWasteGeneratedInOperationsItem,
  useUpdateSupplierQuestionnaireWasteGeneratedInOperationsItem,
} from "../../../../../../../hooks/query/supplier-questionnaire/tabs/categories";

function CategoryFiveConsumptionBased({ tabKey, currentElement, callMethod, isPreview }) {
  const wasteGeneratedInOperationsKey = "wasteGeneratedInOperations";
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { questionnaireId } = currentElement;
  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const colHeaders = [
    t(`inputs.${wasteGeneratedInOperationsKey}.wasteActivity.name`),
    t(`inputs.${wasteGeneratedInOperationsKey}.wasteType.name`),
    t(`inputs.${wasteGeneratedInOperationsKey}.disposalMethod.name`),
    t(`inputs.${wasteGeneratedInOperationsKey}.weight`)
  ];
  const attributesOrder = ["wasteActivity", "wasteType", "disposalMethod", "weight"];
  const FORM_INPUT_KEY = "wasteGeneratedInOperations";

  const {
    handleSubmit
  } = useForm();

  const onSubmit = (() => {
    if (!isPreview) {
      callMethod(null);
    }
  });

  const prepareSectionValues = useCallback(items => {
    const wasteActivityKey = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.wasteActivity.options";
    const wasteTypeKey = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.wasteType.options";
    const disposalMethodKey = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.disposalMethod.options";

    return items.map(item => {
      return {
        id: item.id ?? null,
        wasteActivityValue: item.wasteActivity,
        wasteActivity: i18n.exists(`${wasteActivityKey}.${item.wasteActivity}`) ?
          t(`inputs.wasteGeneratedInOperations.wasteActivity.options.${item.wasteActivity}`) : item.wasteActivity ?? "",
        wasteTypeValue: item.wasteType,
        wasteType: i18n.exists(`${wasteTypeKey}.${item.wasteActivity}.${item.wasteType}`) ?
          t(`inputs.wasteGeneratedInOperations.wasteType.options.${item.wasteActivity}.${item.wasteType}`) : item.wasteType ?? "",
        disposalMethodValue: item.disposalMethod,
        disposalMethod: i18n.exists(`${disposalMethodKey}.${item.disposalMethod}`) ?
          t(`inputs.wasteGeneratedInOperations.disposalMethod.options.${item.disposalMethod}`) : item.disposalMethod ?? "",
        weight: item.weight ?? ""
      };
    });
  }, [i18n, t]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`)
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        wasteActivity: item.wasteActivityValue,
        wasteType: item.wasteTypeValue,
        disposalMethod: item.disposalMethodValue,
        weight: item.weight
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`)
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddSupplierQuestionnaireWasteGeneratedInOperationsItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(supplierQuestionnaireWasteGeneratedInOperationsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateSupplierQuestionnaireWasteGeneratedInOperationsItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(supplierQuestionnaireWasteGeneratedInOperationsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteSupplierQuestionnaireWasteGeneratedInOperationsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(supplierQuestionnaireWasteGeneratedInOperationsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleCategoryModalSubmit = (formData) => {
    const apiData = [];
    formData[categoryModalData.fieldArrayName].forEach(el => apiData.push(el));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      wasteActivity: "",
      wasteType: "",
      disposalMethod: "",
      weight: ""
    };
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={`navForm${tabKey}`} marginTop={4}>
      <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetSupplierQuestionnaireWasteGeneratedInOperationsItems}
        isPreview={isPreview}
        maxHeight={"280"}
        showNoDataLabel={isPreview}
      />

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isCategoryModalOpen && categoryModalData !== null && (
          <WasteGeneratedInOperationsModalWrapper
            tabKey={wasteGeneratedInOperationsKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            getEmptyRow={getEmptyRow}
          />
        )
      }
    </CategoryForm>
  );
}

export default CategoryFiveConsumptionBased;